'use client';

import { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import Logo from '@/components/brand/Logo';
import UserMenu from '@/components/dashboard/UserMenu';
import { useAuth } from '@/hooks/useAuth';
import { useAuthStore } from '@/store/authStore';
import { cn } from '@/lib/utils';

interface NavLink {
  label: string;
  href: string;
  isButton?: boolean;
  isExternal?: boolean;
}

const NAV_LINKS: NavLink[] = [
  { label: 'Platform', href: '/#platform' },
  { label: 'Jobs', href: '/jobs' },
  { label: 'Courses', href: '/courses' },
  { label: 'Knowledge hub', href: '/knowledge-hub' },
  { label: 'About', href: '/about' },
];

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

function usePublicNavAuth() {
  const { user, isAuthenticated, isLoading } = useAuth();
  const isHydrated = useAuthStore((state) => state.isHydrated);
  return {
    user,
    isAuthenticated,
    ready: isHydrated && !isLoading,
  };
}

export default function PublicNav() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const pathname = usePathname();
  const previousPathname = useRef<string | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const previousActiveElement = useRef<HTMLElement | null>(null);

  // Close drawer on route navigation (skip initial mount)
  useEffect(() => {
    if (previousPathname.current !== null && pathname !== previousPathname.current) {
      setMobileOpen(false);
    }
    previousPathname.current = pathname;
  }, [pathname]);

  // Lock body scroll, focus first element, and restore focus on close/unmount
  useEffect(() => {
    if (typeof document === 'undefined') return;

    if (mobileOpen) {
      previousActiveElement.current = document.activeElement as HTMLElement;
      document.body.style.overflow = 'hidden';

      const panel = panelRef.current;
      if (panel) {
        const focusables = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
        const visibleFocusables = focusables.filter((el) => el.offsetParent !== null);
        if (visibleFocusables.length > 0) {
          visibleFocusables[0].focus();
        }
      }
    } else {
      document.body.style.overflow = '';
      const element = previousActiveElement.current;
      if (element && element.isConnected) {
        element.focus();
      }
      previousActiveElement.current = null;
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileOpen]);

  // Escape to close, Tab to trap focus inside the drawer
  useEffect(() => {
    if (!mobileOpen || typeof document === 'undefined') return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setMobileOpen(false);
        return;
      }

      if (event.key !== 'Tab') return;

      const panel = panelRef.current;
      if (!panel) return;

      const focusables = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
        (el) => el.offsetParent !== null
      );
      if (focusables.length === 0) return;

      const first = focusables[0];
      const last = focusables[focusables.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [mobileOpen]);

  return (
    <>
      <nav className="sticky top-0 z-50 border-b border-neon-cyan/10 bg-obsidian/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
          <Logo href="/" />

          {/* Desktop links */}
          <div className="hidden items-center gap-7 md:flex">
            {NAV_LINKS.map((link) => (
              <NavItem key={link.href} link={link} />
            ))}
            <DesktopAuthActions />
          </div>

          {/* Mobile menu button */}
          <button
            type="button"
            aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileOpen}
            onClick={() => setMobileOpen((value) => !value)}
            className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-neon-cyan/20 text-ash transition hover:border-neon-cyan/40 hover:text-neon-cyan md:hidden"
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </nav>

      {/* Mobile drawer */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            key="public-mobile-nav"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="fixed inset-0 z-50 md:hidden"
            role="dialog"
            aria-modal="true"
          >
            <motion.button
              type="button"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
              className="absolute inset-0 bg-obsidian/90"
              aria-label="Close menu"
              onClick={() => setMobileOpen(false)}
            />
            <motion.div
              ref={panelRef}
              initial={{ x: '100%', opacity: 0.8 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: '100%', opacity: 0.8 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className="absolute right-0 top-0 h-full w-72 border-l border-neon-cyan/10 bg-obsidian p-6 shadow-2xl"
            >
              <div className="flex items-center justify-between">
                <Logo href="/" />
                <button
                  type="button"
                  aria-label="Close menu"
                  onClick={() => setMobileOpen(false)}
                  className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-neon-cyan/20 text-ash transition hover:border-neon-cyan/40 hover:text-neon-cyan"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <MobileUserInfo />

              <div className="mt-8 flex flex-col gap-2">
                {NAV_LINKS.map((link) => (
                  <Link
                    key={link.href}
                    href={link.href}
                    onClick={() => setMobileOpen(false)}
                    className="flex min-h-11 items-center rounded-lg px-3 text-base font-medium text-ash transition hover:bg-neon-cyan/8 hover:text-neon-cyan"
                  >
                    {link.label}
                  </Link>
                ))}
                <hr className="my-3 border-white/10" />
                <MobileAuthActions closeMenu={() => setMobileOpen(false)} />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

function DesktopAuthActions() {
  const { user, isAuthenticated, ready } = usePublicNavAuth();

  if (!ready) {
    return (
      <div className="hidden items-center gap-4 md:flex" aria-hidden="true">
        <div className="h-5 w-14 animate-pulse rounded-md bg-white/10" />
        <div className="h-9 w-24 animate-pulse rounded-full bg-white/10" />
      </div>
    );
  }

  if (!isAuthenticated || !user) {
    return (
      <div className="hidden items-center gap-4 md:flex">
        <Link
          href="/login"
          className="text-sm font-medium text-ash hover:text-neon-cyan"
        >
          Sign in
        </Link>
        <Link
          href="/register"
          className="rounded-full bg-neon-cyan px-5 py-2.5 text-sm font-semibold text-obsidian transition hover:bg-neon-cyan/90"
        >
          Start now
        </Link>
      </div>
    );
  }

  const userName =
    user.fullName?.trim() ||
    (user.firstName && user.lastName
      ? `${user.firstName} ${user.lastName}`
      : user.firstName) ||
    user.email;

  return (
    <div className="hidden items-center gap-4 md:flex">
      <Link
        href="/dashboard"
        className="text-sm font-medium text-ash hover:text-neon-cyan"
      >
        Dashboard
      </Link>
      <UserMenu
        role={user.role}
        userName={userName}
        userEmail={user.email}
        className="border-neon-cyan/20 bg-obsidian text-ash hover:border-neon-cyan hover:text-neon-cyan"
      />
    </div>
  );
}

function MobileAuthActions({ closeMenu }: { closeMenu: () => void }) {
  const { isAuthenticated, ready } = usePublicNavAuth();

  if (!ready) {
    return (
      <>
        <div className="h-11 animate-pulse rounded-lg bg-white/10" />
        <div className="h-11 animate-pulse rounded-full bg-white/10" />
      </>
    );
  }

  if (!isAuthenticated) {
    return (
      <>
        <Link
          href="/login"
          onClick={closeMenu}
          className="flex min-h-11 items-center rounded-lg px-3 text-base font-medium text-ash transition hover:bg-neon-cyan/8 hover:text-neon-cyan"
        >
          Sign in
        </Link>
        <Link
          href="/register"
          onClick={closeMenu}
          className="flex min-h-11 items-center justify-center rounded-full bg-neon-cyan px-3 py-2.5 text-base font-semibold text-obsidian transition hover:bg-neon-cyan/90"
        >
          Start now
        </Link>
      </>
    );
  }

  return (
    <Link
      href="/dashboard"
      onClick={closeMenu}
      className="flex min-h-11 items-center rounded-lg px-3 text-base font-medium text-ash transition hover:bg-neon-cyan/8 hover:text-neon-cyan"
    >
      Dashboard
    </Link>
  );
}

function MobileUserInfo() {
  const { user, isAuthenticated, ready } = usePublicNavAuth();

  if (!ready || !isAuthenticated || !user) {
    return null;
  }

  const userName =
    user.fullName?.trim() ||
    (user.firstName && user.lastName
      ? `${user.firstName} ${user.lastName}`
      : user.firstName) ||
    user.email;

  return (
    <div className="mt-6 flex items-center gap-3 border-b border-white/10 pb-6">
      <UserMenu
        role={user.role}
        userName={userName}
        userEmail={user.email}
        className="border-neon-cyan/20 bg-obsidian text-ash hover:border-neon-cyan hover:text-neon-cyan"
      />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold text-foreground">
          {userName}
        </p>
        <p className="truncate text-xs text-muted-foreground">{user.email}</p>
      </div>
    </div>
  );
}

function NavItem({ link }: { link: NavLink }) {
  const isAnchor = link.href.includes('#');
  const baseClass =
    'text-sm font-medium text-ash hover:text-neon-cyan transition';

  if (link.isExternal) {
    return (
      <a
        href={link.href}
        target="_blank"
        rel="noopener noreferrer"
        className={baseClass}
      >
        {link.label}
      </a>
    );
  }

  return (
    <Link
      href={link.href}
      className={cn(baseClass, isAnchor && 'scroll-smooth')}
    >
      {link.label}
    </Link>
  );
}
